import { EMPTY, moduleByKey, routes } from '@pmai/contracts';
import { Button, DataTable, DetailGrid, EmptyState, PageHeader, Spinner } from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { NotesPanel } from '../../entities/notes/notes-panel';
import { useProjectColumns } from '../../entities/projects/project-columns';
import { ApiError } from '../../shared/api/client';
import { useApiQuery } from '../../shared/api/hooks';
import { Can } from '../../shared/auth/session';
import { CardSection } from '../../shared/card-section';
import { useErrorText } from '../../shared/i18n';
import { useDocumentTitle } from '../../shared/use-document-title';
import { NotFoundPage } from '../../shared/system-pages';
import { CustomerDialog } from './customer-dialog';
import { ExternalLink } from '../../shared/external-link';
import { CustomerStatusBadge, useCustomerProjects } from './customer-parts';
import { CustomerProductsSection } from './customer-products';
import { CustomerTasksSection } from './customer-tasks';

const text = (value: string | null) => value ?? EMPTY;

/**
 * Customer card — details FR-CUST-008/009, projects FR-CUST-010, products FR-CUST-011/012,
 * tasks FR-CUST-013…016, notes FR-CUST-017…019.
 */
export function CustomerDetailPage() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const { id = '' } = useParams();
  const customer = useApiQuery(routes.customers.get, { params: { id } }, { retry: false });
  const [editing, setEditing] = useState(false);
  useDocumentTitle(customer.data?.name);

  if (customer.isPending) return <Spinner label={t('common.loading')} />;
  if (customer.isError) {
    return customer.error instanceof ApiError && customer.error.code === 'NOT_FOUND' ? (
      <NotFoundPage title={t('customers.notFound')} />
    ) : (
      <NotFoundPage title={errorText(customer.error)} />
    );
  }

  const c = customer.data;
  const f = (field: string) => t(`customers.fields.${field}`);
  const listPath = moduleByKey('customers').path ?? '/';
  return (
    <>
      <PageHeader
        title={c.name}
        back={{ to: listPath, label: t('customers.back') }}
        actions={
          <Can perm="customers.edit">
            <Button variant="secondary" icon="pencil" onClick={() => setEditing(true)}>
              {t('common.edit')}
            </Button>
          </Can>
        }
      />
      <div className="flex flex-col gap-5">
        <CardSection id="customer.details" title={t('customers.sections.details')}>
          <DetailGrid
            items={[
              { label: f('name'), value: c.name },
              { label: f('regNr'), value: text(c.regNr) },
              { label: f('status'), value: <CustomerStatusBadge status={c.status} /> },
              { label: f('contactPerson'), value: text(c.contactPerson) },
              {
                label: f('email'),
                value: c.email ? (
                  <a href={`mailto:${c.email}`} className="text-brand-700 hover:underline">
                    {c.email}
                  </a>
                ) : (
                  EMPTY
                ),
              },
              { label: f('phone'), value: text(c.phone) },
              { label: f('address'), value: text(c.address) },
              { label: f('website'), value: <ExternalLink href={c.website} /> },
              {
                label: f('contract'),
                value: <ExternalLink href={c.contractUrl} label={t('customers.openFolder')} />,
              },
              {
                label: f('notes'),
                value: <p className="whitespace-pre-line">{text(c.notes)}</p>,
                wide: true,
              },
            ]}
          />
        </CardSection>
        <CardSection id="customer.projects" title={t('customers.sections.projects')}>
          <CustomerProjects customerId={c.id} />
        </CardSection>
        <CustomerProductsSection customerId={c.id} />
        <CustomerTasksSection customerId={c.id} />
        <CardSection id="customer.notes" title={t('customers.sections.notes')}>
          <NotesPanel owner={{ kind: 'customer', id: c.id }} />
        </CardSection>
      </div>
      <CustomerDialog open={editing} customer={c} onClose={() => setEditing(false)} />
    </>
  );
}

function CustomerProjects({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const c = useProjectColumns();
  const projects = useCustomerProjects(customerId);
  return (
    <DataTable
      label={t('customers.sections.projects')}
      rows={projects.data?.rows}
      rowKey={(p) => p.id}
      loading={projects.isPending}
      empty={<EmptyState icon="folder" title={t('customers.noProjects')} />}
      columns={[c.name, c.status, c.priority, c.startDate, c.plannedFinish, c.completion]}
    />
  );
}
